<!-- ======================================================================
     Reservations Dashboard Shell — v2 team-landing page (Phase 2)
     Daily landing for guest-services / reservations agents (the comms hub).
     All CSS classes use the `rdsh-` prefix to avoid collisions with other
     team dashboards.
     ====================================================================== -->

<!-- 4px team color stripe (Reservations = indigo #5a6cd6) -->
<div class="rdsh-team-banner"></div>

<!-- ─── Greeting + Quick Actions ─────────────────────────────────────── -->
<div class="rdsh-greet">
  <div class="rdsh-greet__left">
    <h1 class="rdsh-greet__title">
      Reservations
      <span class="rdsh-greet__sub">{{ greetingTime }}</span>
    </h1>
    <div class="rdsh-greet__context">
      <a class="rdsh-inline" [routerLink]="['/unified-inbox/7']">Guest inbox</a>
      <span class="rdsh-sep">·</span>
      <a class="rdsh-inline" [routerLink]="['/inbox-list-vertical', 8]">Owner inbox</a>
      <span class="rdsh-sep">·</span>
      <a class="rdsh-inline" [routerLink]="['/inbox-list-vertical', 9]">BizDev inbox</a>
      <span class="rdsh-sep">·</span>
      <a class="rdsh-inline" [routerLink]="['/tapechart']">Tape chart</a>
    </div>
  </div>
  <div class="rdsh-quick-actions">
    <a class="rdsh-qa-btn" [routerLink]="['/tapechart']">Tape Chart</a>
    <a class="rdsh-qa-btn" [routerLink]="['/task-phone-add']">Send SMS</a>
    <a class="rdsh-qa-btn rdsh-qa-btn--primary" [routerLink]="['/quote']">+ New Quote</a>
  </div>
</div>

<!-- ─── KPI Action Chips strip — Reservations-specific groups ─────────
     Each chip group mirrors a high-frequency action for guest services.
     Same chip pattern as Ops shell for visual consistency across team
     dashboards.
     ────────────────────────────────────────────────────────────────────── -->
<div class="rdsh-chips-band">
  <!-- Inbox -->
  <div class="rdsh-chip-group">
    <span class="rdsh-chip-group__label">Inbox</span>
    <a class="rdsh-chip" [routerLink]="['/unified-inbox/7']" [queryParams]="{status:'unread'}">Unread</a>
    <a class="rdsh-chip rdsh-chip--err" [routerLink]="['/unified-inbox/7']" [queryParams]="{status:'unread', age:'24h'}">Over 24h</a>
    <a class="rdsh-chip rdsh-chip--primary" [routerLink]="['/unified-inbox/7']">Open Inbox</a>
  </div>
  <!-- Quotes -->
  <div class="rdsh-chip-group">
    <span class="rdsh-chip-group__label">Quotes</span>
    <a class="rdsh-chip" [routerLink]="['/quote-list']" [queryParams]="{owner:'me'}">Mine</a>
    <a class="rdsh-chip rdsh-chip--err" [routerLink]="['/quote-list']" [queryParams]="{followup:'overdue'}">Overdue</a>
    <a class="rdsh-chip rdsh-chip--primary" [routerLink]="['/quote']">+ New Quote</a>
  </div>
  <!-- Reviews -->
  <div class="rdsh-chip-group">
    <span class="rdsh-chip-group__label">Reviews</span>
    <a class="rdsh-chip rdsh-chip--err" [routerLink]="['/review-list']" [queryParams]="{response:'missing', sentiment:'negative'}">Negative unanswered</a>
    <a class="rdsh-chip" [routerLink]="['/review-list']">All</a>
  </div>
  <!-- Refunds -->
  <div class="rdsh-chip-group">
    <span class="rdsh-chip-group__label">Refunds</span>
    <a class="rdsh-chip rdsh-chip--warn" [routerLink]="['/guest-refunds']" [queryParams]="{status:'awaiting-approval'}">Approve</a>
    <a class="rdsh-chip" [routerLink]="['/guest-refunds']">All</a>
  </div>
</div>

<!-- ─── My Tasks band (universal) ────────────────────────────────────── -->
<div class="rdsh-tasks-band">
  <div class="rdsh-tasks-band__head">
    <span class="rdsh-tasks-band__heading">My Tasks</span>
    <span class="rdsh-tasks-band__count" *ngIf="tasksUrgent > 0 || tasksOpen > 0">
      <ng-container *ngIf="tasksUrgent > 0">{{ tasksUrgent }} urgent</ng-container>
      <ng-container *ngIf="tasksUrgent > 0 && tasksOpen > 0"> · </ng-container>
      <ng-container *ngIf="tasksOpen > 0">{{ tasksOpen }} open</ng-container>
    </span>
    <a class="rdsh-tasks-band__view-all" [routerLink]="['/task-list']">View all tasks</a>
  </div>
  <div class="rdsh-tasks-band__body" *ngIf="tasks.length > 0">
    <a class="rdsh-task-row" *ngFor="let t of tasks" [routerLink]="['/task-edit', t.taskId]">
      <span class="rdsh-task-prio" [ngClass]="taskPrioClass(t.priority)">{{ t.priority }}</span>
      <span class="rdsh-task-desc">{{ t.description }}</span>
      <span class="rdsh-task-due" *ngIf="t.dueDateLabel">{{ t.dueDateLabel }}</span>
    </a>
  </div>
  <div class="rdsh-tasks-band__empty" *ngIf="tasks.length === 0 && !tasksLoading">
    No open tasks assigned to you
  </div>
  <div class="rdsh-tasks-band__empty" *ngIf="tasksLoading">
    Loading tasks...
  </div>
</div>

<!-- ─── Multi-context inbox preview (placeholder — wires up in follow-up) ─── -->
<div class="rdsh-inbox-preview">
  <div class="rdsh-inbox-preview__heading">Inbox preview (multi-context)</div>
  <div class="rdsh-inbox-preview__grid">
    <a class="rdsh-inbox-card" [routerLink]="['/unified-inbox/7']">
      <div class="rdsh-inbox-card__icon">👤</div>
      <div class="rdsh-inbox-card__body">
        <div class="rdsh-inbox-card__title">Guest</div>
        <div class="rdsh-inbox-card__hint">Unread messages from guests</div>
      </div>
      <span class="rdsh-inbox-card__arrow">→</span>
    </a>
    <a class="rdsh-inbox-card" [routerLink]="['/inbox-list-vertical', 8]">
      <div class="rdsh-inbox-card__icon">🔑</div>
      <div class="rdsh-inbox-card__body">
        <div class="rdsh-inbox-card__title">Owner</div>
        <div class="rdsh-inbox-card__hint">Owner threads requiring attention</div>
      </div>
      <span class="rdsh-inbox-card__arrow">→</span>
    </a>
    <a class="rdsh-inbox-card" [routerLink]="['/inbox-list-vertical', 9]">
      <div class="rdsh-inbox-card__icon">📈</div>
      <div class="rdsh-inbox-card__body">
        <div class="rdsh-inbox-card__title">BizDev</div>
        <div class="rdsh-inbox-card__hint">Sales-pipeline conversations</div>
      </div>
      <span class="rdsh-inbox-card__arrow">→</span>
    </a>
  </div>
</div>

<!-- ─── Surfaced folder cards ────────────────────────────────────────── -->
<div class="rdsh-folders-section">
  <div class="rdsh-folders-heading">Folders most used by Reservations</div>
  <div class="rdsh-folders-grid">

    <div class="rdsh-folder-card rdsh-folder-card--guests">
      <a class="rdsh-folder-head" [routerLink]="['/reservations']">
        <span class="rdsh-folder-ic">G</span>
        Guests
      </a>
      <div class="rdsh-folder-body">
        <a class="rdsh-folder-lk" [routerLink]="['/reservations']">Reservations</a>
        <a class="rdsh-folder-lk" [routerLink]="['/quote-list']">Quotes</a>
        <a class="rdsh-folder-lk" [routerLink]="['/tapechart']">Tape Chart</a>
        <a class="rdsh-folder-lk" [routerLink]="['/best-fit-list']">Best Fit Finder</a>
        <a class="rdsh-folder-lk" [routerLink]="['/guest-master-profile-list']">Guest Master Profile</a>
        <a class="rdsh-folder-lk" [routerLink]="['/review-list']">Reviews</a>
        <a class="rdsh-folder-lk" [routerLink]="['/guest-refunds']">Refunds</a>
      </div>
    </div>

    <div class="rdsh-folder-card rdsh-folder-card--comms">
      <a class="rdsh-folder-head" [routerLink]="['/unified-inbox/7']">
        <span class="rdsh-folder-ic">C</span>
        Communications
      </a>
      <div class="rdsh-folder-body">
        <a class="rdsh-folder-lk" [routerLink]="['/unified-inbox/7']">Unified Inbox</a>
        <a class="rdsh-folder-lk" [routerLink]="['/msg-list']">Messages</a>
        <a class="rdsh-folder-lk" [routerLink]="['/msg-template-list']">Templates</a>
        <a class="rdsh-folder-lk" [routerLink]="['/mktg-letter-list']">Marketing Letters</a>
        <a class="rdsh-folder-lk" [routerLink]="['/task-phone-add']">Send SMS Tool</a>
        <a class="rdsh-folder-lk" [routerLink]="['/tracked-links']">Tracked Links</a>
        <a class="rdsh-folder-lk" [routerLink]="['/contact-list']">Contacts</a>
      </div>
    </div>

    <div class="rdsh-folder-card rdsh-folder-card--cross">
      <a class="rdsh-folder-head" [routerLink]="['/inbox-list-vertical', 8]">
        <span class="rdsh-folder-ic">X</span>
        Owners &amp; BizDev
        <span class="rdsh-folder-note">cross-team</span>
      </a>
      <div class="rdsh-folder-body">
        <a class="rdsh-folder-lk" [routerLink]="['/inbox-list-vertical', 8]">Owner Comm</a>
        <a class="rdsh-folder-lk" [routerLink]="['/inbox-list-vertical', 9]">BizDev Comm</a>
        <a class="rdsh-folder-lk" [routerLink]="['/owner-profile-list']">Owner Profiles</a>
        <a class="rdsh-folder-lk" [routerLink]="['/property-profile']">Property Profile</a>
        <a class="rdsh-folder-lk" [routerLink]="['/owner-review-list']">Owner Reviews</a>
      </div>
    </div>

  </div>
</div>
